import React, { createContext, useState, useContext, useEffect } from 'react';
import axiosInstance from '../lib/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Periodically sync user data with server to detect external updates (e.g., admin editing user)
  useEffect(() => {
    if (!user?.id) return;

    const syncUserData = async () => {
      try {
        const response = await axiosInstance.get(`/users/${user.id}`);
        const serverUser = response.data;
        
        // Compare and update if there are changes
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        if (serverUser.name !== currentUser.name || 
            serverUser.role !== currentUser.role || 
            serverUser.email !== currentUser.email) {
          
          console.log('User data updated from server:', {
            old: { name: currentUser.name, role: currentUser.role, email: currentUser.email },
            new: { name: serverUser.name, role: serverUser.role, email: serverUser.email }
          });
          
          // Update localStorage and state
          const updatedUser = {
            ...currentUser,
            name: serverUser.name,
            role: serverUser.role,
            email: serverUser.email,
            assigned_projects: serverUser.assigned_projects || currentUser.assigned_projects || []
          };
          
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);
        }
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.error('Failed to sync user data:', error);
      }
    };

    // Sync immediately on mount
    syncUserData();

    // Then sync every 60 seconds
    const interval = setInterval(syncUserData, 60000);

    return () => clearInterval(interval);
  }, [user?.id]);

  const login = async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role) => {
    const response = await axiosInstance.post('/auth/register', { email, password, name, role });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/login';
  };

  const updateUser = (updatedUserData) => {
    // Get current user from localStorage to ensure we have the latest
    const currentStoredUser = localStorage.getItem('user');
    const currentUser = currentStoredUser ? JSON.parse(currentStoredUser) : user;
    
    // Merge updates
    const newUser = { ...currentUser, ...updatedUserData };
    
    // Save to both localStorage and state
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
    
    console.log('User updated:', newUser); // Debug log
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};